import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Award, Users, Target, ArrowRight, Wrench, Clock, Phone, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { aboutUs, features, services as mockServices } from '../data/mock';
import { getServices } from '../services/productApi';

const About = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState(mockServices);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const servicesData = await getServices();
        if (servicesData?.length > 0) {
          setServices(servicesData);
        }
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    loadServices();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Desktop: Kacheln links – Video mitte – Kacheln rechts */}
      <section className="relative bg-white pt-2">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          
          {/* Desktop Layout: 3 Spalten */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-[1fr_2fr_1fr] gap-5 xl:gap-6">
              
              {/* Linke Spalte: 2 Kacheln */}
              <div className="flex flex-col gap-4 h-[400px] xl:h-[450px]">
                {/* Kachel 1: Qualität */}
                <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                  <CardContent className="h-full flex flex-col items-center justify-center text-center p-5">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-3">
                      <Award className="text-red-600" size={26} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Qualität & Zuverlässigkeit</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Nur hochwertige Baumaschinen von renommierten Herstellern in unserem Sortiment
                    </p>
                  </CardContent>
                </Card>

                {/* Kachel 2: Kundennähe */}
                <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                  <CardContent className="h-full flex flex-col items-center justify-center text-center p-5">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-3">
                      <Users className="text-red-600" size={26} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Kundenorientierung</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Individuelle Beratung und Lösungen, die genau auf Ihre Bedürfnisse zugeschnitten sind
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Mittlere Spalte: Video */}
              <div className="relative w-full overflow-hidden rounded-xl h-[400px] xl:h-[450px]">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster="/videos/video2-poster.jpg"
                  className="w-full h-full object-cover object-center"
                  webkit-playsinline="true"
                >
                  <source src="/videos/video2.mp4" type="video/mp4" />
                </video>
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end">
                  <div className="px-6 pb-6 w-full text-center">
                    <h1 className="text-3xl xl:text-4xl font-bold text-white mb-2">{t('nav.ueberUns')}</h1>
                    <p className="text-sm xl:text-base text-gray-200">
                      Ihr zuverlässiger Partner für Baumaschinen
                    </p>
                  </div>
                </div>
              </div>

              {/* Rechte Spalte: 2 Kacheln */}
              <div className="flex flex-col gap-4 h-[400px] xl:h-[450px]">
                {/* Kachel 3: Faire Preise */}
                <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                  <CardContent className="h-full flex flex-col items-center justify-center text-center p-5">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-3">
                      <Target className="text-red-600" size={26} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Faire Preise</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Wettbewerbsfähige Preise durch Direktimport und schlanke Strukturen
                    </p>
                  </CardContent>
                </Card>

                {/* Kachel 4: Professioneller Service */}
                <Card className="flex-1 rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)] border border-gray-100 transition-all">
                  <CardContent className="h-full flex flex-col items-center justify-center text-center p-5">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-3">
                      <Handshake className="text-red-600" size={26} />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Professioneller Service</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Persönliche Betreuung von der Beratung bis zur Auslieferung und darüber hinaus
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Unterer Banner */}
            <div className="mt-5 xl:mt-6">
              <Card className="rounded-xl overflow-hidden bg-gradient-to-r from-red-600 to-red-700 shadow-lg border-0">
                <CardContent className="py-6 xl:py-8">
                  <div className="flex items-center justify-center">
                    <div className="w-14 h-14 xl:w-16 xl:h-16 bg-white/20 rounded-full flex items-center justify-center mr-5">
                      <Award className="text-white" size={28} />
                    </div>
                    <div className="text-center">
                      <h3 className="text-2xl xl:text-3xl font-bold text-white mb-1">Über Jannesmann Baumaschinen</h3>
                      <p className="text-base xl:text-lg text-red-100">
                        {aboutUs.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Mobile/Tablet Layout: Video volle Breite */}
          <div className="lg:hidden">
            <div className="relative w-full overflow-hidden rounded-xl">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video2-poster.jpg"
                className="w-full h-[280px] sm:h-[400px] md:h-[500px] object-cover object-center"
                webkit-playsinline="true"
              >
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
              
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent flex items-end">
                <div className="px-6 sm:px-12 pb-6 sm:pb-10">
                  <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-4">{t('nav.ueberUns')}</h1>
                  <p className="text-sm sm:text-lg text-gray-200">
                    Ihr zuverlässiger Partner für Baumaschinen
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-4 text-center">
            {aboutUs.title}
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed text-center mb-8">
            {aboutUs.description}
          </p>

          {/* Info-Karten - nur auf Mobile/Tablet anzeigen (auf Desktop sind sie im Hero) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 lg:hidden">
            <Card className="rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] border border-gray-100 transition-all">
              <CardContent className="pt-6 text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-red-600" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Qualität & Zuverlässigkeit</h3>
                <p className="text-sm text-gray-600">
                  Nur hochwertige Baumaschinen von renommierten Herstellern in unserem Sortiment.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] border border-gray-100 transition-all">
              <CardContent className="pt-6 text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-red-600" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Kundenorientierung</h3>
                <p className="text-sm text-gray-600">
                  Individuelle Beratung und Lösungen, die genau auf Ihre Bedürfnisse zugeschnitten sind.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] border border-gray-100 transition-all">
              <CardContent className="pt-6 text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="text-red-600" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Faire Preise</h3>
                <p className="text-sm text-gray-600">
                  Wettbewerbsfähige Preise durch Direktimport und schlanke Strukturen.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-xl overflow-hidden bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] border border-gray-100 transition-all">
              <CardContent className="pt-6 text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Handshake className="text-red-600" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Professioneller Service</h3>
                <p className="text-sm text-gray-600">
                  Persönliche Betreuung von der Beratung bis zur Auslieferung und darüber hinaus.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Über uns Banner - nur auf Mobile/Tablet */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 md:p-8 text-center text-white shadow-xl lg:hidden">
            <div className="flex items-center justify-center mb-4">
              <Award className="text-white mr-3" size={36} />
              <h3 className="text-2xl md:text-3xl font-bold">Jannesmann Baumaschinen</h3>
            </div>
            <p className="text-lg md:text-xl">
              {aboutUs.description}
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose Us - desktop visible */}
      <section className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="bg-gray-50 rounded-xl p-6 md:p-8">
            <div className="text-center mb-8">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Warum Jannesmann Baumaschinen?
              </h2>
              <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Erfahrung, Qualität und Service - darauf können Sie sich verlassen.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="text-red-600" size={24} />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Langjährige Erfahrung</h3>
                  <p className="text-sm text-gray-600">
                    Unser Fachwissen basiert auf jahrelanger Erfahrung im Bereich Baumaschinen und Erdbau.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Users className="text-red-600" size={24} />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Persönlicher Service</h3>
                  <p className="text-sm text-gray-600">
                    Individuelle Beratung und Lösungen, die genau auf Ihre Bedürfnisse zugeschnitten sind.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white">
                <CardContent className="pt-6 text-center">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Target className="text-red-600" size={24} />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">Fokus auf Qualität</h3>
                  <p className="text-sm text-gray-600">
                    Nur hochwertige Maschinen von renommierten Herstellern in unserem Portfolio.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Einheitlicher Info-Bereich: 4 Vorteile + 3 Leistungen + CTA */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="bg-gray-50 rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
            
            {/* 4 Vorteile */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feature, index) => {
                const IconComponent = {
                  Award,
                  Wrench,
                  Clock,
                  HeadphonesIcon: Phone
                }[feature.icon];
                
                return (
                  <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                    <CardContent className="pt-6 pb-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                          <IconComponent className="text-red-600" size={24} />
                        </div>
                        <h3 className="font-semibold text-sm sm:text-lg mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* 3 Leistungen */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {services.map((service) => (
                <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        {service.id === 1 && <Award className="text-red-600" size={24} />}
                        {service.id === 2 && <Phone className="text-red-600" size={24} />}
                        {service.id === 3 && <Wrench className="text-red-600" size={24} />}
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">{service.description}</p>
                      <ul className="space-y-1 text-left w-full">
                        {service.features.slice(0, 3).map((feat, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm">
                            <CheckCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-gray-700">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA - Bereit für Ihr Projekt */}
            <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <ArrowRight className="text-red-600" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl mb-2">{t('cta.readyForProject')}</h3>
                  <p className="text-gray-600 text-sm sm:text-base mb-4 max-w-xl">
                    {t('cta.contactUs')}
                  </p>
                  <Link to="/kontakt">
                    <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                      {t('cta.getInTouch')}
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Zwei Videos am Ende */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video2-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
            </div>

            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/videos/video3-poster.jpg"
                className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover"
                webkit-playsinline="true"
              >
                <source src="/videos/video3.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
